<template>
  <AppShell>
    <!-- Profile Overview Section -->
    <div class="mb-6">
      <div class="flex items-center space-x-6 mb-6">
        <!-- Avatar -->
        <img :alt="`${profile.fullname}'s avatar`" class="rounded-full border-4 border-line shadow-lg"
          :src="profilePicture" height="100" width="100" />

        <!-- Text Information -->
        <div>
          <h1 class="text-3xl font-bold text-white">{{ profile.username }}</h1>
          <p class="text-lg text-ink-muted">{{ profile.fullname }}</p>
        </div>
      </div>

      <!-- Navigation Tabs -->
      <div class="flex items-center border-b border-line">
        <button
          :class="[
            'px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab === 'profile' ? 'text-brand border-brand' : 'text-ink-muted border-transparent hover:text-white',
          ]"
          @click="activeTab = 'profile'"
        >
          Profile
        </button>
        <button
          :class="[
            'px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab === 'billing' ? 'text-brand border-brand' : 'text-ink-muted border-transparent hover:text-white',
          ]"
          @click="activeTab = 'billing'"
        >
          Billing
        </button>
        <button
          :class="[
            'px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors',
            activeTab === 'settings' ? 'text-brand border-brand' : 'text-ink-muted border-transparent hover:text-white',
          ]"
          @click="activeTab = 'settings'"
        >
          Settings
        </button>
      </div>
    </div>

    <!-- Tab Content: Profile -->
    <div v-if="activeTab === 'profile'" class="flex flex-wrap gap-4 justify-center">
      <!-- Left Side: Profile Info + Subscription -->
      <div class="flex flex-col gap-4 w-full lg:w-1/3">
        <!-- Profile Information -->
        <div class="card p-6 flex flex-col h-full">
          <h3 class="text-lg font-bold text-white border-b border-line pb-2 mb-4">Profile Information</h3>
          <div class="flex flex-col justify-center flex-1">
            <div class="text-ink-muted space-y-3">
              <p class="flex justify-between">
                <span class="font-bold text-white">Full Name:</span>
                <span>{{ profile.fullname }}</span>
              </p>
              <p class="flex justify-between">
                <span class="font-bold text-white">Username:</span>
                <span>{{ profile.username }}</span>
              </p>
              <p class="flex justify-between">
                <span class="font-bold text-white">Email:</span>
                <span>{{ profile.email }}</span>
              </p>
              <p class="flex justify-between">
                <span class="font-bold text-white">Points:</span>
                <span>{{ profile.points }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Subscription Status -->
        <div class="card p-6 flex flex-col h-full">
          <h3 class="text-lg font-bold text-white border-b border-line pb-2 mb-4 text-center">Subscription Status</h3>
          <div class="flex flex-col justify-center items-center flex-1">
            <p class="text-lg font-bold text-center"
              :class="profile.isSubscribed ? 'text-success' : 'text-brand'">
              {{ profile.isSubscribed ? 'Subscribed User' : 'Free User' }}
            </p>
            <p v-if="profile.isSubscribed" class="text-ink-muted text-center mt-2">
              Expires at: <span class="font-bold text-white">{{ formatDate(profile.subscriptionEndDate) }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Right Side: Rank Card -->
      <div class="card p-6 flex flex-col w-full lg:w-3/5">
        <div class="text-center w-full mb-6">
          <h3 class="text-lg font-bold text-white border-b border-line pb-2">Rank Progress</h3>
        </div>

        <!-- Badge and Details -->
        <div class="flex items-center gap-8 w-full max-w-4xl mx-auto">
          <!-- Large Badge -->
          <div class="flex justify-center w-1/3">
            <img :src="badgeUrl" alt="Rank Badge" class="h-64 w-64 rounded-full shadow-lg" />
          </div>

          <!-- Rank Details -->
          <div class="flex flex-col justify-center text-center flex-1">
            <h3 class="text-5xl font-bold mb-4 text-yellow-400">{{ rank }}</h3>
            <p class="text-xl text-ink-muted mb-6">{{ profile.points }} Points</p>

            <!-- Progress Bar -->
            <div class="relative w-full max-w-md mx-auto mb-6">
              <div class="h-4 bg-surface-lighter rounded-full">
                <div class="h-full bg-brand-gradient rounded-full" :style="{ width: progressBarWidth }"></div>
              </div>
            </div>

            <!-- Next Rank -->
            <p class="text-lg text-ink-muted">
              Next Rank:
              <span class="font-bold text-white">{{ nextRank }}</span>
              <span class="text-sm"> ({{ nextRankPoints }} Points Needed)</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab Content: Billing -->
    <div v-if="activeTab === 'billing'" class="flex flex-col gap-6">
      <div class="card p-6 flex flex-col">
        <h3 class="text-lg font-bold text-white border-b border-line pb-2 mb-4 text-center">Billing History</h3>

        <div v-if="loading" class="text-center text-ink-muted py-12">
          <i class="fas fa-spinner fa-spin text-2xl mb-3 text-brand"></i>
          <p>Loading...</p>
        </div>

        <!-- Empty state -->
        <div v-else-if="billings.length === 0" class="text-center py-12">
          <i class="fas fa-receipt text-4xl text-ink-faint mb-3"></i>
          <p class="text-ink-muted">No billing available yet.</p>
          <a href="/subscribe" class="btn-secondary mt-4 inline-flex">
            <i class="fas fa-crown text-yellow-400"></i> View Pricing
          </a>
        </div>

        <div v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- Card for each billing -->
            <div v-for="billing in paginatedBillings" :key="billing.id"
              class="bg-surface-light border border-line rounded-xl p-6 flex flex-col justify-between">
              <div>
                <p class="text-sm text-ink-muted mb-2">
                  <strong class="text-white">Invoice ID:</strong> <span class="font-bold text-white">{{ billing.id }}</span>
                </p>
                <p class="text-sm text-ink-muted mb-2">
                  <strong class="text-white">Subscription:</strong> <span class="capitalize">{{ billing.subscriptionType }}</span>
                </p>
                <p class="text-sm text-ink-muted mb-2">
                  <strong class="text-white">Status: </strong>
                  <span :class="statusClass(billing.status)">{{ billing.status }}</span>
                </p>
                <p class="text-sm text-ink-muted mb-2">
                  <strong class="text-white">Amount:</strong> <span class="font-bold text-white">Rp {{ billing.amount }}</span>
                </p>
                <p class="text-sm text-ink-muted mb-2">
                  <strong class="text-white">Created At:</strong> <span class="font-bold text-white">{{ formatDateTime(billing.createdAt) }}</span>
                </p>
              </div>
              <button class="btn-primary mt-4 w-full justify-center"
                @click="viewInvoice(billing.id)">
                View Invoice
              </button>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div v-if="totalPages > 1" class="flex justify-center mt-6 space-x-2">
            <button class="btn-secondary"
              :disabled="currentPage === 1" @click="currentPage--">
              Previous
            </button>
            <span class="px-4 py-2 bg-surface-light border border-line rounded-lg text-white text-sm">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <button class="btn-secondary"
              :disabled="currentPage === totalPages" @click="currentPage++">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab Content: Settings -->
    <div v-if="activeTab === 'settings'" class="flex flex-wrap gap-4">
      <!-- Update Profile Card -->
      <div class="card p-6 flex-1 min-w-[300px]">
        <h3 class="text-lg font-bold text-white border-b border-line pb-2 mb-4 text-center">Update Profile</h3>

        <form @submit.prevent="updateProfile">
          <div class="mb-4">
            <label class="block text-ink-muted mb-2" for="fullname">Full Name</label>
            <input v-model="updateProfileData.fullname" id="fullname" type="text" placeholder="Enter full name"
              class="input" />
          </div>

          <div class="mb-4">
            <label class="block text-ink-muted mb-2" for="username">Username</label>
            <input v-model="updateProfileData.username" id="username" type="text" placeholder="Enter username"
              class="input" />
          </div>

          <button :disabled="!updateProfileData.fullname || !updateProfileData.username" type="submit"
            class="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            Update Profile
          </button>
        </form>
      </div>

      <!-- Change Password Card -->
      <div class="card p-6 flex-1 min-w-[300px]">
        <h3 class="text-lg font-bold text-white border-b border-line pb-2 mb-4 text-center">Change Password</h3>

        <form @submit.prevent="updatePassword">
          <div class="mb-4">
            <label class="block text-ink-muted mb-2" for="oldPassword">Old Password</label>
            <input v-model="oldPassword" id="oldPassword" type="password" placeholder="Enter old password"
              class="input" />
          </div>

          <div class="mb-4">
            <label class="block text-ink-muted mb-2" for="newPassword">New Password</label>
            <input v-model="newPassword" id="newPassword" type="password" placeholder="Enter new password"
              class="input" />
          </div>

          <div class="mb-4">
            <label class="block text-ink-muted mb-2" for="confirmPassword">Confirm New Password</label>
            <input v-model="confirmPassword" id="confirmPassword" type="password" placeholder="Confirm new password"
              class="input" />
            <p v-if="passwordMismatch" class="text-brand text-sm mt-1">Passwords do not match.</p>
          </div>

          <button :disabled="!oldPassword || !newPassword || passwordMismatch" type="submit"
            class="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            Change Password
          </button>
        </form>
      </div>
    </div>
  </AppShell>
</template>

<script>
import apiClient from "@/axiosConfig";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import AppShell from "@/components/ui-components/AppShell.vue";
import { ranks } from "@/config/ranks.js";

toastr.options = {
  positionClass: "toast-bottom-right",
  closeButton: true,
  progressBar: true,
  timeOut: 5000,
};

export default {
  name: "ManageProfile",
  components: {
    AppShell,
  },
  data() {
    return {
      activeTab: this.$route.query.tab || "profile",
      profile: {
        fullname: "",
        username: "",
        email: "",
        points: 0,
      },
      updateProfileData: {
        fullname: "",
        username: "",
      },
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      points: 0,
      isSubscribed: false,
      rank: "Kiddie",
      nextRank: "Wannabe",
      nextRankPoints: 501,
      progressBarWidth: "0%",
      badgeUrl: "",
      rankLevels: ranks,
      billings: [],
      loading: false,
      currentPage: 1,
      itemsPerPage: 6,
    };
  },
  computed: {
    rankBadgeUrl() {
      const currentRank = ranks.find((rank) => rank.name === this.rank);
      return currentRank ? currentRank.badgeUrl : "/assets/badges/default.webp";
    },
    profilePicture() {
      if (!this.profile.id) return "/assets/profile/default.png";
      const userImageIndex = (parseInt(this.profile.id.replace(/-/g, ""), 16) % 20) + 1;
      return `/assets/profile/${userImageIndex}.png`;
    },
    passwordMismatch() {
      return this.newPassword !== this.confirmPassword; // Check if passwords do not match
    },
    paginatedBillings() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.billings.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.billings.length / this.itemsPerPage);
    },
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab) {
        this.activeTab = newTab;
      }
    },
  },
  methods: {
    async fetchProfile() {
      try {
        const response = await apiClient.get("/users/current");
        this.profile = response.data.user;
        this.updateProfileData.fullname = this.profile.fullname;
        this.updateProfileData.username = this.profile.username;
        this.points = this.profile.points;
        this.isSubscribed = this.profile.isSubscribed;
        this.calculateRank();
      } catch (error) {
        toastr.error("Failed to load profile information.", "Error");
      }
    },
    async updateProfile() {
      try {
        await apiClient.put(
          "/users/self",
          {
            fullname: this.updateProfileData.fullname,
            username: this.updateProfileData.username,
          }
        );
        toastr.success("Profile updated successfully!", "Success");
        this.profile.fullname = this.updateProfileData.fullname;
        this.profile.username = this.updateProfileData.username;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to update profile. Please try again.";
        toastr.error(errorMessage, "Error");
      }
    },
    async updatePassword() {
      if (this.passwordMismatch) {
        toastr.error("Passwords do not match.", "Error");
        return;
      }

      try {
        await apiClient.post("/auth/change-password", {
          oldPassword: this.oldPassword,
          newPassword: this.newPassword,
        });
        toastr.success("Password updated successfully!", "Success");
        this.oldPassword = "";
        this.newPassword = "";
        this.confirmPassword = "";
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to change password. Please try again.";
        toastr.error(errorMessage, "Error");
      }
    },
    async fetchBillingHistory() {
      this.loading = true;
      try {
        const response = await apiClient.get("/subscription/list/current");
        this.billings = response.data;
      } catch (error) {
        console.error("Error fetching billing history:", error);
        toastr.error("Failed to load billing history.", "Error");
      } finally {
        this.loading = false;
      }
    },
    viewInvoice(billingId) {
      this.$router.push(`/invoice/${billingId}`);
    },
    statusClass(status) {
      switch (status) {
        case "pending":
          return "text-yellow-500 font-bold";
        case "completed":
          return "text-green-500 font-bold";
        case "denied":
          return "text-red-500 font-bold";
        default:
          return "text-gray-400";
      }
    },
    formatDateTime(date) {
      return new Date(date).toLocaleString();
    },
    calculateRank() {
      const currentRank = ranks.find((rank) => this.points >= rank.min && this.points <= rank.max);
      const nextRank = ranks.find((rank) => rank.min > currentRank.max);

      this.rank = currentRank.name;
      this.badgeUrl = currentRank.badgeUrl;
      this.nextRank = nextRank ? nextRank.name : "Max Rank Achieved";
      this.nextRankPoints = nextRank ? nextRank.min - this.points : 0;
      this.progressBarWidth = `${((this.points - currentRank.min) / (currentRank.max - currentRank.min)) * 100}%`;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A"; // Handle cases where the date is not available
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-US', options);
    },
  },
  mounted() {
    this.fetchProfile();
    this.fetchBillingHistory();
  },
};
</script>
